import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FlatList, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions} from 'react-native';
import Modal from 'react-native-modal';
import {useTranslation} from 'react-i18next';
import Observables from '@happy-gastro/global-observables';
import {useOfflineSyncContext} from './OfflineSyncContext';
import {toDisplayText} from './displayText';
import {WINDOW_MEDIUM_MIN, getModalWidth} from './windowSizeClass';

const ESCALATION_AFTER_MS = 60 * 1000;

const formatDuration = (startedAt, t) => {
    if (!startedAt) {
        return t('pending_order_unknown_duration');
    }

    const diffMs = Math.max(0, Date.now() - new Date(startedAt).getTime());
    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes <= 0) {
        return t('duration_seconds', { seconds });
    }

    return t('duration_minutes_seconds', { minutes, seconds });
};

const getOrderReference = (item) => {
    // Every candidate goes through toDisplayText: userInfo.name is a {first,last}
    // object on online orders and a raw object as a <Text> child crashes the app.
    return toDisplayText(item?.order_data?.currentTable?.name)
        || toDisplayText(item?.order_data?.currentTable?.code)
        || toDisplayText(item?.order_data?.userData?.userInfo?.name)
        || toDisplayText(item?.order_data?.orderId)
        || toDisplayText(item?.id);
};

const getStatusLabel = (status, t) => {
    if (status === 'Failed') {
        return t('pending_order_status_needs_attention');
    }

    if (status === 'Syncing') {
        return t('pending_order_status_syncing');
    }

    return t('pending_order_status_waiting');
};

const PendingOrderDialog = () => {
    const {t} = useTranslation();
    const {width} = useWindowDimensions();
    // Phones keep 92%; 600dp+ windows get a capped, centred card.
    const containerWidth = width >= WINDOW_MEDIUM_MIN ? getModalWidth(width, {max: 560, ratio: 0.7}) : '92%';
    const {
        isOnline,
        pendingOrders,
        loadPendingOrders,
        retryAll,
        retryOne,
        removeOrder,
        failedCount,
        pendingCount,
        syncingCount,
    } = useOfflineSyncContext();

    const [isVisible, setIsVisible] = useState(false);
    const [snoozedUntil, setSnoozedUntil] = useState(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        loadPendingOrders();
    }, [loadPendingOrders]);

    useEffect(() => {
        const interval = globalThis.setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => globalThis.clearInterval(interval);
    }, []);

    useEffect(() => {
        const openDialog = async () => {
            await loadPendingOrders();
            setIsVisible(true);
        };

        Observables.register('offlineSync:attentionRequired', openDialog);
        return () => {
            if (typeof Observables.remove === 'function') {
                Observables.remove('offlineSync:attentionRequired');
            }
        };
    }, [loadPendingOrders]);

    const escalatedOrders = useMemo(() => {
        return (pendingOrders || []).filter((item) => {
            const baseTime = item?.first_failure_at || item?.timestamp;

            if (!baseTime) {
                return false;
            }

            return (now - new Date(baseTime).getTime()) >= ESCALATION_AFTER_MS;
        });
    }, [pendingOrders, now]);

    useEffect(() => {
        const isSnoozed = Boolean(snoozedUntil && now < snoozedUntil);

        if (escalatedOrders.length > 0 && !isSnoozed) {
            setIsVisible(true);
            return;
        }

        if (escalatedOrders.length === 0) {
            setIsVisible(false);
        }
    }, [escalatedOrders, snoozedUntil, now]);

    const handleClose = useCallback(() => {
        setSnoozedUntil(Date.now() + ESCALATION_AFTER_MS);
        setIsVisible(false);
    }, []);

    const handleRetryAll = useCallback(async () => {
        await retryAll();
        await loadPendingOrders();
        setIsVisible(false);
    }, [retryAll, loadPendingOrders]);

    const handleRetryOne = useCallback(async (id) => {
        await retryOne(id);
        await loadPendingOrders();
    }, [retryOne, loadPendingOrders]);

    const handleRemove = useCallback(async (id) => {
        await removeOrder(id);
        await loadPendingOrders();
    }, [removeOrder, loadPendingOrders]);

    const renderItem = useCallback(({item}) => {
        const baseTime = item?.first_failure_at || item?.timestamp;
        const tableLabel = getOrderReference(item);
        const statusLabel = getStatusLabel(item?.sync_status, t);
        const itemCount = (item?.order_data?.cartItems?.length || 0) + (item?.order_data?.alreadySentCartItems?.length || 0);
        const badgeStyle = item?.sync_status === 'Failed' ? styles.statusFailed : styles.statusPending;

        return (
            <View style={styles.orderCard}>
                <View style={styles.orderHeaderRow}>
                    <Text style={styles.orderTitle}>{tableLabel}</Text>
                    <View style={[styles.statusBadge, badgeStyle]}>
                        <Text style={styles.statusBadgeText}>{statusLabel}</Text>
                    </View>
                </View>

                <Text style={styles.orderMeta}>{t('pending_order_waiting_label', {duration: formatDuration(baseTime, t)})}</Text>
                <Text style={styles.orderMeta}>{t('pending_order_items_label', {count: itemCount})}</Text>

                {!!item?.error_message && (
                    <Text style={styles.orderError}>{t('pending_order_error_label', {error: toDisplayText(item.error_message)})}</Text>
                )}

                <View style={styles.rowActions}>
                    <TouchableOpacity
                        style={[styles.smallButton, styles.secondaryButton]}
                        onPress={() => handleRetryOne(item.id)}
                    >
                        <Text style={styles.smallButtonText}>{t('retry')}</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                        style={[styles.smallButton, styles.dangerButton]}
                        onPress={() => handleRemove(item.id)}
                    >
                        <Text style={styles.smallButtonText}>{t('remove')}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }, [handleRemove, handleRetryOne, t]);

    return (
        <Modal
            isVisible={isVisible}
            onBackdropPress={handleClose}
            useNativeDriver
            hideModalContentWhileAnimating
            style={styles.modal}
        >
            <View style={[styles.container, {width: containerWidth}]}>
                <Text style={styles.title}>{t('pending_order_dialog_title')}</Text>
                <Text style={styles.subtitle}>
                    {t('pending_order_dialog_subtitle')}
                </Text>

                <View style={styles.summaryRow}>
                    <Text style={styles.summaryText}>{t('pending_order_connection_label', {status: isOnline ? t('pending_order_state_online') : t('pending_order_state_offline')})}</Text>
                    <Text style={styles.summaryText}>
                        {t('pending_order_summary_counts', {pending: pendingCount, failed: failedCount, syncing: syncingCount})}
                    </Text>
                </View>

                <FlatList
                    data={escalatedOrders}
                    keyExtractor={(item) => String(item.id)}
                    renderItem={renderItem}
                    style={styles.list}
                    contentContainerStyle={styles.listContent}
                />

                <View style={styles.footerActions}>
                    <TouchableOpacity style={[styles.footerButton, styles.ghostButton]} onPress={handleClose}>
                        <Text style={styles.ghostButtonText}>{t('later')}</Text>
                    </TouchableOpacity>

                    <TouchableOpacity style={[styles.footerButton, styles.primaryButton]} onPress={handleRetryAll}>
                        <Text style={styles.primaryButtonText}>{t('pending_order_retry_all')}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modal: {
        justifyContent: 'center',
        alignItems: 'center',
        margin: 0,
    },
    container: {
        backgroundColor: '#fff',
        borderRadius: 18,
        padding: 20,
        maxHeight: '82%',
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.18,
        shadowRadius: 18,
        elevation: 12,
    },
    title: {
        fontSize: 22,
        fontWeight: '700',
        color: '#1E1F20',
        marginBottom: 8,
    },
    subtitle: {
        fontSize: 14,
        color: '#575757',
        lineHeight: 21,
        marginBottom: 14,
    },
    summaryRow: {
        backgroundColor: '#F8F3EA',
        borderRadius: 12,
        padding: 12,
        marginBottom: 14,
        gap: 4,
    },
    summaryText: {
        fontSize: 13,
        color: '#2A3042',
        fontWeight: '600',
    },
    list: {
        maxHeight: 420,
    },
    listContent: {
        paddingBottom: 4,
    },
    orderCard: {
        borderWidth: 1,
        borderColor: '#EFE6D8',
        backgroundColor: '#FFFDFC',
        borderRadius: 14,
        padding: 14,
        marginBottom: 12,
    },
    orderHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
        gap: 8,
    },
    orderTitle: {
        flex: 1,
        fontSize: 16,
        fontWeight: '700',
        color: '#2A3042',
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
    },
    statusPending: {
        backgroundColor: '#FDE1B6',
    },
    statusFailed: {
        backgroundColor: '#FDE0E0',
    },
    statusBadgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#2A3042',
    },
    orderMeta: {
        fontSize: 13,
        color: '#4E5665',
        marginBottom: 4,
    },
    orderError: {
        marginTop: 4,
        fontSize: 13,
        color: '#B42318',
        fontWeight: '600',
    },
    rowActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 12,
        gap: 8,
    },
    smallButton: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 10,
    },
    secondaryButton: {
        backgroundColor: '#F1B44C',
    },
    dangerButton: {
        backgroundColor: '#D92D20',
    },
    smallButtonText: {
        color: '#fff',
        fontWeight: '700',
        fontSize: 13,
    },
    footerActions: {
        marginTop: 16,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 10,
    },
    footerButton: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 12,
    },
    ghostButton: {
        backgroundColor: '#EEE8DD',
    },
    primaryButton: {
        backgroundColor: '#2A3042',
    },
    ghostButtonText: {
        color: '#2A3042',
        fontWeight: '700',
    },
    primaryButtonText: {
        color: '#fff',
        fontWeight: '700',
    },
});

export default PendingOrderDialog;